/**
 * Optimistic UI (web.md §3): change the query cache NOW, run the engine call, and on an error put back
 * exactly what was changed. The engine's `changed` event reconciles the success case (invalidate.ts).
 *
 *   await optimistic(queryClient, (p) => {
 *     p.patchRows(['list'], [id], (row) => ({ ...row, read: true }));
 *   }, () => engine.messages.setRead([id], true));
 */

import type { InfiniteData, QueryClient, QueryKey } from '@tanstack/react-query';
import type { MessageId, MessagePage, MessageRow } from '@digester/core';

export interface Patcher {
  /** Replace the data of every cached query under `prefix` (skips queries without data). */
  patch<T>(prefix: QueryKey, updater: (old: T) => T): void;
  /** Update rows with these ids in every cached message list under `prefix` (pages or infinite pages). */
  patchRows(prefix: QueryKey, ids: readonly MessageId[], updater: (row: MessageRow) => MessageRow): void;
  /** Remove rows from every cached message list under `prefix` whose list no longer contains them. */
  removeRows(prefix: QueryKey, ids: readonly MessageId[], options?: { where?: (key: QueryKey) => boolean; countUnread?: boolean }): void;
}

type ListData = MessagePage | InfiniteData<MessagePage>;

function isPage(value: unknown): value is MessagePage {
  return typeof value === 'object' && value !== null && Array.isArray((value as MessagePage).rows);
}

function isInfinite(value: unknown): value is InfiniteData<MessagePage> {
  return typeof value === 'object' && value !== null && Array.isArray((value as InfiniteData<MessagePage>).pages);
}

function mapPages(data: ListData, fn: (page: MessagePage) => MessagePage): ListData {
  if (isInfinite(data)) return { ...data, pages: data.pages.map((p) => (isPage(p) ? fn(p) : p)) };
  return fn(data);
}

export function createPatcher(qc: QueryClient): Patcher & { rollback(): void; touched(): QueryKey[] } {
  const saved: Array<{ key: QueryKey; data: unknown }> = [];
  const savedKeys = new Set<string>();

  const remember = (key: QueryKey, data: unknown): void => {
    const id = JSON.stringify(key);
    if (savedKeys.has(id)) return;
    savedKeys.add(id);
    saved.push({ key, data });
  };

  const patch = <T>(prefix: QueryKey, updater: (old: T) => T): void => {
    for (const [key, data] of qc.getQueriesData<T>({ queryKey: prefix })) {
      if (data === undefined) continue;
      remember(key, data);
      qc.setQueryData<T>(key, updater(data));
    }
  };

  return {
    patch,
    patchRows(prefix, ids, updater) {
      const wanted = new Set<string>(ids);
      patch<unknown>(prefix, (old) => {
        if (!isPage(old) && !isInfinite(old)) return old;
        return mapPages(old, (page) => ({ ...page, rows: page.rows.map((row) => (wanted.has(row.id) ? updater(row) : row)) }));
      });
    },
    removeRows(prefix, ids, options = {}) {
      const gone = new Set<string>(ids);
      for (const [key, data] of qc.getQueriesData<unknown>({ queryKey: prefix })) {
        if (data === undefined || (!isPage(data) && !isInfinite(data))) continue;
        if (options.where && !options.where(key)) continue;
        remember(key, data);
        let removed = 0;
        let removedUnread = 0;
        let removedMinutes = 0;
        const next = mapPages(data, (page) => {
          const rows = page.rows.filter((row) => {
            if (!gone.has(row.id)) return true;
            removed++;
            if (!row.read) {
              removedUnread++;
              removedMinutes += row.readingMinutes;
            }
            return false;
          });
          return { ...page, rows };
        });
        // Totals describe the whole query and live on every page: adjust them all.
        qc.setQueryData(key, mapPages(next, (page) => ({
          ...page,
          total: Math.max(0, page.total - removed),
          unread: Math.max(0, page.unread - removedUnread),
          unreadMinutes: Math.max(0, page.unreadMinutes - removedMinutes),
        })));
      }
    },
    rollback() {
      for (const { key, data } of saved.slice().reverse()) qc.setQueryData(key, data);
    },
    touched: () => saved.map((s) => s.key),
  };
}

/**
 * Apply `change` to the cache synchronously, then run `run`. On failure the cache is restored and the
 * error is rethrown (the caller shows a toast). In-flight refetches of the touched queries are
 * cancelled so they cannot overwrite the optimistic state with stale data.
 */
export async function optimistic<T>(qc: QueryClient, change: (p: Patcher) => void, run: () => Promise<T>): Promise<T> {
  const patcher = createPatcher(qc);
  change(patcher);
  for (const key of patcher.touched()) void qc.cancelQueries({ queryKey: key, exact: true }, { revert: false });
  try {
    return await run();
  } catch (error) {
    patcher.rollback();
    throw error;
  }
}
